/**
 * TanStack Query 钩子层 —— 前端访问后端的唯一通道。
 *
 * 原则（specs/14 §3 / RULES §11）：
 * - 服务端实体只走这里，禁止进 zustand；
 * - **失败即失败**：查询出错直接抛出，由视图渲染 `ErrorState`（CONSTITUTION D1）；
 *   唯一例外是显式离线演示模式 `VITE_USE_MOCK=1`，它不是"回落"而是人主动打开的开关；
 * - 写操作一律不回落：宁可报错，也不能假装成功。
 */

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import {
  fetchRoleChangeLogs,
  logout as apiLogout,
  me as fetchMe,
  setUserRole as apiSetUserRole,
  switchView as apiSwitchView,
  type SessionUser,
} from "./auth";
import { USE_MOCK, api } from "./client";
import * as mock from "./mock";
import { useUiStore } from "@/store/ui";
import type {
  AdoptInput,
  Artifact,
  ArtifactCreateInput,
  ArtifactDiff,
  ArtifactLineage,
  AudienceAnalytics,
  AudiencePlaythroughs,
  CapabilityCatalog,
  CommunityIdea,
  CommunityIdeas,
  CommunityPulse,
  CostSummary,
  ExportJob,
  ExportPreview,
  Follows,
  FollowState,
  Gate,
  HealthStatus,
  IdeaLikeResult,
  Json,
  Methodology,
  MethodologyList,
  MethodologyMigration,
  MethodologyUpgradePreview,
  ModelSettings,
  MyPlaythroughs,
  PlayerType,
  PlaySummary,
  Playthrough,
  Profile,
  ProfileImpact,
  Project,
  ProjectAudit,
  ProjectCreateInput,
  ProjectOverview,
  PublishReadiness,
  Reachability,
  ReadyStatus,
  ResolveResult,
  Review,
  ReviewInput,
  RoleChangeLog,
  RollbackInput,
  Run,
  Scheme,
  SetRoleInput,
  Simulation,
  SimulationCompare,
  SimulationSummary,
  Source,
  SystemMetrics,
  TransitionInput,
  ValidationResult,
} from "./types";

/**
 * 当前项目 ID：来自 UI store（specs/14 §5.2）。
 * 未选定项目时为空串，此时所有项目级查询保持 disabled，由视图渲染空态引导新建。
 */
export function useCurrentProjectId(): string {
  return useUiStore((state) => state.projectId);
}

/**
 * 数据来源选择器。
 *
 * - 默认：直接走远端，失败即抛出（**不回落**）；
 * - 离线演示模式（`VITE_USE_MOCK=1`）：返回本地基线数据。
 *
 * 注意这不是"失败后回落"——判断发生在请求**之前**，且是显式开关。
 */
function source<T>(remote: () => Promise<T>, offline?: () => T): () => Promise<T> {
  if (!USE_MOCK) return remote;
  if (!offline) {
    return () => Promise.reject(new Error("离线演示模式未提供该数据源，请关闭 VITE_USE_MOCK"));
  }
  return async () => offline();
}

export const queryKeys = {
  projects: ["projects"] as const,
  project: (id: string) => ["projects", id] as const,
  overview: (id: string) => ["projects", id, "overview"] as const,
  profile: (id: string) => ["projects", id, "profile"] as const,
  schemes: (id: string) => ["projects", id, "schemes"] as const,
  gates: (id: string) => ["projects", id, "gates"] as const,
  artifacts: (id: string, type?: string, status?: string) =>
    ["projects", id, "artifacts", type ?? "all", status ?? "all"] as const,
  artifact: (artifactId: string) => ["artifacts", artifactId] as const,
  artifactVersions: (artifactId: string) => ["artifacts", artifactId, "versions"] as const,
  artifactDiff: (artifactId: string, against: string) =>
    ["artifacts", artifactId, "diff", against] as const,
  artifactLineage: (artifactId: string) => ["artifacts", artifactId, "lineage"] as const,
  artifactReviews: (artifactId: string) => ["artifacts", artifactId, "reviews"] as const,
  validations: (id: string) => ["projects", id, "validations"] as const,
  simulations: (id: string) => ["projects", id, "simulations"] as const,
  /**
   * 模拟历史（F-5）。
   *
   * ⚠️ **必须与 `simulations` 分开**：`simulations` 这个键上缓存的是
   * `POST /simulations` 返回的**单个 `Simulation`**（含 `paths`），
   * 而列表返回的是 `SimulationSummary[]`。共用一个键会让 `setQueryData` 与
   * `useQuery` 互相覆盖——症状是列表里出现"缺了 paths 的那一条"或反之。
   */
  simulationHistory: (id: string) => ["projects", id, "simulations", "history"] as const,
  reachability: (id: string) => ["projects", id, "reachability"] as const,
  simulationReport: (simulationId: string) => ["simulations", simulationId] as const,
  runs: (id: string) => ["projects", id, "runs"] as const,
  run: (runId: string) => ["runs", runId] as const,
  cost: (id: string) => ["projects", id, "cost"] as const,
  audit: (id: string) => ["projects", id, "audit"] as const,
  sources: (id: string) => ["projects", id, "sources"] as const,
  exports: (id: string) => ["projects", id, "exports"] as const,
  exportJob: (exportId: string) => ["exports", exportId] as const,
  exportPreview: (id: string) => ["projects", id, "exports", "preview"] as const,
  simulationCompare: (baseId: string, targetId: string) =>
    ["simulations", baseId, "compare", targetId] as const,
  playerTypes: ["player-types"] as const,
  methodology: ["methodology", "current"] as const,
  methodologyList: ["methodology", "list"] as const,
  methodologyVersion: (name: string, version: string) =>
    ["methodology", "version", name, version] as const,
  methodologyPreview: (id: string) => ["projects", id, "methodology", "preview"] as const,
  systemMetrics: ["system", "metrics"] as const,
  health: ["system", "health"] as const,
  ready: ["system", "ready"] as const,
  capabilities: ["system", "capabilities"] as const,
  modelSettings: ["settings", "model"] as const,
  audienceAnalytics: (id: string, groupBy: string) =>
    ["projects", id, "audience", "analytics", groupBy] as const,
  audiencePlaythroughs: (id: string) => ["projects", id, "audience", "playthroughs"] as const,
  communityIdeas: (sort: string) => ["community", "ideas", sort] as const,
  session: ["session"] as const,
  users: ["auth", "users"] as const,
  /** 公开作品目录（`GET /plays`，`specs/15` §12）。 */
  plays: ["plays"] as const,
  publishReadiness: (id: string) => ["projects", id, "publish", "readiness"] as const,
  /** 本人游玩列表（续玩，`GET /audience/playthroughs`）。 */
  myPlaythroughs: (status: string) => ["audience", "playthroughs", status] as const,
  /** 本人的追更订阅（跨设备，`GET /audience/follows`，`ADR-024`）。 */
  follows: ["audience", "follows"] as const,
  /** 「本周共创数据」脉冲（`GET /community/pulse`，`specs/15` §10 第 8 条）。 */
  communityPulse: ["community", "pulse"] as const,
  /** 角色变更记录（`GET /auth/role-changes`，`ADR-025` D5）。`userId` 缺省＝服务端按身份定归属。 */
  roleChanges: (userId?: string) => ["auth", "role-changes", userId ?? "self"] as const,
};

/* ------------------------------------------------------------------ 项目 */

export function useProjects(options?: Partial<UseQueryOptions<Project[]>>) {
  return useQuery<Project[]>({
    queryKey: queryKeys.projects,
    queryFn: source(
      () => api.get<Project[]>("/projects"),
      () => mock.mockProjects,
    ),
    staleTime: 60_000,
    ...options,
  });
}

export function useProject(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Project>({
    queryKey: queryKeys.project(pid),
    queryFn: source(
      () => api.get<Project>(`/projects/${pid}`),
      () => mock.mockProjects.find((item) => item.id === pid) ?? mock.mockProject,
    ),
    enabled: Boolean(pid),
    staleTime: 60_000,
  });
}

export function useCreateProject() {
  const client = useQueryClient();
  return useMutation({
    // 失败由 `ProjectSwitcher` 就地呈现（`createProject.isError`）→ 公共兜底跳过（见 api/query-client.ts）
    meta: { errorHandled: true },
    mutationFn: (payload: ProjectCreateInput) => api.post<Project>("/projects", payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

export function useUpdateProject(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    // 失败由 `ProjectSwitcher` 就地呈现（`updateProject.isError`）
    meta: { errorHandled: true },
    mutationFn: (payload: { name?: string; status?: string; synopsis?: string }) =>
      api.patch<Project>(`/projects/${pid}`, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.projects });
      void client.invalidateQueries({ queryKey: queryKeys.project(pid) });
      // 简介会出现在广场卡片上、也从总览聚合回传（`ADR-021`）——两处都要刷新。
      void client.invalidateQueries({ queryKey: queryKeys.plays });
      void client.invalidateQueries({ queryKey: queryKeys.overview(pid) });
    },
  });
}

/* ---------------------------------------- 发布（specs/15 §12 · ADR-020） */

/** 公开作品目录（`GET /plays`）：**不需登录**，只列已发布作品。 */
export function usePlays() {
  return useQuery<PlaySummary[]>({
    queryKey: queryKeys.plays,
    queryFn: source(
      () => api.get<PlaySummary[]>("/plays"),
      () => mock.mockPlays,
    ),
    staleTime: 60_000,
  });
}

/** 发布就绪检查（只读，不抛错）：界面在点击发布前呈现前提。 */
export function usePublishReadiness(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<PublishReadiness>({
    queryKey: queryKeys.publishReadiness(pid),
    queryFn: source(
      () => api.get<PublishReadiness>(`/projects/${pid}/publish/readiness`),
      () => mock.mockPublishReadiness,
    ),
    enabled: Boolean(pid),
    staleTime: 30_000,
  });
}

/** 申请上架（editor；幂等）：`unpublished → pending`。失败由项目总览就地呈现。 */
export function usePublishProject(projectId?: string) {
  const client = useQueryClient();
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: () => api.post<Project>(`/projects/${pid}/publish`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.projects });
      void client.invalidateQueries({ queryKey: queryKeys.project(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.plays });
      void client.invalidateQueries({ queryKey: queryKeys.publishReadiness(pid) });
    },
  });
}

/** 批准上架（`head_writer`/`admin`；幂等）：`pending → published`（`ADR-022`）。 */
export function useApprovePublish(projectId?: string) {
  const client = useQueryClient();
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (comment: string) =>
      api.post<Project>(`/projects/${pid}/publish/approve`, { comment }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.projects });
      void client.invalidateQueries({ queryKey: queryKeys.project(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.plays });
      void client.invalidateQueries({ queryKey: queryKeys.publishReadiness(pid) });
    },
  });
}

/** 驳回上架（`head_writer`/`admin`）：`pending → unpublished`（`ADR-022`）。 */
export function useRejectPublish(projectId?: string) {
  const client = useQueryClient();
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (comment: string) =>
      api.post<Project>(`/projects/${pid}/publish/reject`, { comment }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.projects });
      void client.invalidateQueries({ queryKey: queryKeys.project(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.plays });
      void client.invalidateQueries({ queryKey: queryKeys.publishReadiness(pid) });
    },
  });
}

/** 下架 / 撤回（editor；幂等）：任意状态 → `unpublished`。 */
export function useUnpublishProject(projectId?: string) {
  const client = useQueryClient();
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: () => api.post<Project>(`/projects/${pid}/unpublish`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.projects });
      void client.invalidateQueries({ queryKey: queryKeys.project(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.plays });
      void client.invalidateQueries({ queryKey: queryKeys.publishReadiness(pid) });
    },
  });
}

/**
 * 上传 / 替换作品封面（editor；multipart，字段名 `file`）（`ADR-023`）。
 *
 * 走同一条 `api` 出口（`client.ts` 对 `FormData` 不设 Content-Type、不 JSON 序列化）；
 * 成功后失效项目 / 总览 / 目录三处缓存——`cover_url` 带内容哈希，换了就是新地址。
 */
export function useUploadCover(projectId?: string) {
  const client = useQueryClient();
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api.post<Project>(`/projects/${pid}/cover`, form);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.projects });
      void client.invalidateQueries({ queryKey: queryKeys.project(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.overview(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.plays });
    },
  });
}

export function useOverview(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<ProjectOverview>({
    queryKey: queryKeys.overview(pid),
    queryFn: source(
      () => api.get<ProjectOverview>(`/projects/${pid}/overview`),
      () => mock.mockOverview,
    ),
    enabled: Boolean(pid),
    staleTime: 30_000,
  });
}

/* -------------------------------------------------------------- 分类 G-1 */

export function useProfile(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Profile>({
    queryKey: queryKeys.profile(pid),
    queryFn: source(
      () => api.get<Profile>(`/projects/${pid}/profile`),
      () => mock.mockProfile,
    ),
    enabled: Boolean(pid),
    retry: false,
    staleTime: 30_000,
  });
}

export function useSaveProfile(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    mutationFn: (content: Record<string, Json>) =>
      api.put<Profile>(`/projects/${pid}/profile`, { content }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.profile(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.schemes(pid) });
    },
  });
}

export function useResolveProfile(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useMutation({
    mutationFn: (content: Record<string, Json>) =>
      api.post<ResolveResult>(`/projects/${pid}/profile/resolve`, { content }),
  });
}

export function useSchemes(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Scheme[]>({
    queryKey: queryKeys.schemes(pid),
    queryFn: source(
      () => api.post<Scheme[]>(`/projects/${pid}/profile/schemes`),
      () => mock.mockSchemes,
    ),
    enabled: Boolean(pid),
    staleTime: 60_000,
  });
}

export function useApproveProfile(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    mutationFn: (comment: string) =>
      api.post<Profile>(`/projects/${pid}/profile/approve`, { comment }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.profile(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.gates(pid) });
    },
  });
}

/** 变更影响预览：改动六维前先看会波及哪些 Artifact（specs/14 §4.3）。 */
export function useProfileImpact(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useMutation({
    // 失败由 `ClassifyView` 的弹窗就地呈现（`impact.isError` → `ErrorState`）
    meta: { errorHandled: true },
    mutationFn: (payload: { current: Record<string, Json>; candidate: Record<string, Json> }) =>
      api.post<ProfileImpact>(`/projects/${pid}/profile/impact`, payload),
  });
}

/* ------------------------------------------------------------------ 关口 */

export function useGates(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Gate[]>({
    queryKey: queryKeys.gates(pid),
    queryFn: source(
      () => api.get<Gate[]>(`/projects/${pid}/gates`),
      () => mock.mockGates,
    ),
    enabled: Boolean(pid),
    staleTime: 30_000,
  });
}

export function useApproveGate(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    mutationFn: (args: { gate: string; comment?: string; exceptionReason?: string }) =>
      api.post<Gate>(`/projects/${pid}/gates/${args.gate}`, {
        comment: args.comment ?? "",
        exception_reason: args.exceptionReason ?? "",
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.gates(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.overview(pid) });
    },
  });
}

export function useRejectGate(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    mutationFn: (args: { gate: string; reason: string }) =>
      api.delete<Gate>(`/projects/${pid}/gates/${args.gate}`, {
        body: { reason: args.reason },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.gates(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.overview(pid) });
    },
  });
}

/* ------------------------------------------------------------------ 产物 */

export function useArtifacts(projectId?: string, type?: string, status?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Artifact[]>({
    queryKey: queryKeys.artifacts(pid, type, status),
    // 后端 `GET /projects/{id}/artifacts` 返回**数组**（参数 type / status），非分页信封。
    queryFn: source(
      () =>
        api.get<Artifact[]>(`/projects/${pid}/artifacts`, {
          query: { type, status },
        }),
      () => (type ? mock.mockArtifacts.filter((item) => item.type === type) : mock.mockArtifacts),
    ),
    enabled: Boolean(pid),
    staleTime: 30_000,
  });
}

export function useArtifact(artifactId: string | null) {
  return useQuery<Artifact>({
    queryKey: queryKeys.artifact(artifactId ?? ""),
    queryFn: source(
      () => api.get<Artifact>(`/artifacts/${artifactId}`),
      () => mock.mockArtifacts.find((item) => item.id === artifactId) ?? mock.mockArtifacts[0],
    ),
    enabled: Boolean(artifactId),
    staleTime: 30_000,
  });
}

export function useCreateArtifact(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    // 失败由 `StoryView` 就地呈现（`createArtifact.isError`）
    meta: { errorHandled: true },
    mutationFn: (payload: ArtifactCreateInput) =>
      api.post<Artifact>(`/projects/${pid}/artifacts`, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["projects", pid, "artifacts"] });
    },
  });
}

export function useUpdateArtifact(artifactId: string) {
  const client = useQueryClient();
  return useMutation({
    // 失败已就位呈现：`ArtifactDetail` 传 `mutationError`、`ScriptView` 自带 onError
    meta: { errorHandled: true },
    mutationFn: (payload: { patch: Record<string, Json>; replace?: boolean }) =>
      api.patch<Artifact>(`/artifacts/${artifactId}`, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.artifact(artifactId) });
      void client.invalidateQueries({ queryKey: queryKeys.artifactVersions(artifactId) });
    },
  });
}

export function useArtifactVersions(artifactId: string | null) {
  return useQuery<Artifact[]>({
    queryKey: queryKeys.artifactVersions(artifactId ?? ""),
    queryFn: source(
      () => api.get<Artifact[]>(`/artifacts/${artifactId}/versions`),
      () => mock.mockArtifactVersionsFor(artifactId ?? ""),
    ),
    enabled: Boolean(artifactId),
    staleTime: 30_000,
  });
}

/** 版本差异：`against` 是**另一份 Artifact 的 ID**（不是版本号）。 */
export function useArtifactDiff(artifactId: string | null, against: string | null) {
  return useQuery<ArtifactDiff>({
    queryKey: queryKeys.artifactDiff(artifactId ?? "", against ?? ""),
    queryFn: source(
      () =>
        api.get<ArtifactDiff>(`/artifacts/${artifactId}/diff`, { query: { against: against! } }),
      () => {
        const diff = mock.mockArtifactDiffFor(artifactId ?? "", against ?? "");
        if (!diff) throw new Error("离线数据源中没有这两个产物");
        return diff;
      },
    ),
    enabled: Boolean(artifactId && against),
    staleTime: 60_000,
  });
}

export function useArtifactLineage(artifactId: string | null) {
  return useQuery<ArtifactLineage>({
    queryKey: queryKeys.artifactLineage(artifactId ?? ""),
    queryFn: source(
      () => api.get<ArtifactLineage>(`/artifacts/${artifactId}/lineage`),
      () => {
        const lineage = mock.mockArtifactLineageFor(artifactId ?? "");
        if (!lineage) throw new Error("离线数据源中没有这个产物");
        return lineage;
      },
    ),
    enabled: Boolean(artifactId),
    staleTime: 60_000,
  });
}

export function useArtifactReviews(artifactId: string | null) {
  return useQuery<Review[]>({
    queryKey: queryKeys.artifactReviews(artifactId ?? ""),
    queryFn: source(
      () => api.get<Review[]>(`/artifacts/${artifactId}/reviews`),
      () => mock.mockArtifactReviewsFor(artifactId ?? ""),
    ),
    enabled: Boolean(artifactId),
    staleTime: 15_000,
  });
}

function useArtifactMutation<TArgs>(
  artifactId: string,
  path: string,
  toBody: (args: TArgs) => unknown,
  // 该 hook 的调用点是否**已经就地呈现**失败：`adopt` / `review` / `rollback` 有（`mutationError`
  // 或 `*.isError`），`submit` / `transition` 没有 ⇒ 默认 false，让公共兜底出声（见 api/query-client.ts）。
  opts: { errorHandled?: boolean } = {},
) {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: opts.errorHandled ?? false },
    mutationFn: (args: TArgs) =>
      api.post<Artifact>(`/artifacts/${artifactId}${path}`, toBody(args)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.artifact(artifactId) });
      void client.invalidateQueries({ queryKey: queryKeys.artifactVersions(artifactId) });
      void client.invalidateQueries({ queryKey: queryKeys.artifactLineage(artifactId) });
      void client.invalidateQueries({ queryKey: queryKeys.artifactReviews(artifactId) });
      void client.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/** 采用候选：A2「真人锁定 Canon」的入口。 */
export function useAdoptArtifact(artifactId: string) {
  return useArtifactMutation<AdoptInput>(artifactId, "/adopt", (args) => args, {
    errorHandled: true,
  });
}

export function useSubmitArtifact(artifactId: string) {
  return useArtifactMutation<void>(artifactId, "/submit", () => ({}));
}

export function useReviewArtifact(artifactId: string) {
  return useArtifactMutation<ReviewInput>(artifactId, "/reviews", (args) => args, {
    errorHandled: true,
  });
}

export function useRollbackArtifact(artifactId: string) {
  return useArtifactMutation<RollbackInput>(artifactId, "/rollback", (args) => args, {
    errorHandled: true,
  });
}

export function useTransitionArtifact(artifactId: string) {
  return useArtifactMutation<TransitionInput>(artifactId, "/transition", (args) => args);
}

export function useMarkArtifactStale(artifactId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<{ affected: string[] }>(`/artifacts/${artifactId}/stale`, undefined, {
        query: { reason },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.artifact(artifactId) });
    },
  });
}

/* -------------------------------------------------------------- 校验 / 模拟 */

export function useValidation(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<ValidationResult>({
    queryKey: queryKeys.validations(pid),
    queryFn: source(
      () => api.get<ValidationResult>(`/projects/${pid}/validations/latest`),
      () => mock.mockValidation,
    ),
    enabled: Boolean(pid),
    staleTime: 15_000,
  });
}

export function useRunValidation(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    // 失败提示自带：`Topbar` 与 `ChecksView` 都有 onError toast
    meta: { errorHandled: true },
    mutationFn: (ruleIds?: string[]) =>
      api.post<ValidationResult>(`/projects/${pid}/validations`, {
        rule_ids: ruleIds ?? null,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.validations(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.overview(pid) });
    },
  });
}

export function usePlayerTypes() {
  return useQuery<PlayerType[]>({
    queryKey: queryKeys.playerTypes,
    queryFn: source(
      () => api.get<{ players: PlayerType[] }>("/player-types").then((data) => data.players ?? []),
      () => mock.mockPlayerTypes,
    ),
    staleTime: 5 * 60_000,
  });
}

export function useSimulation(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Simulation>({
    queryKey: queryKeys.simulations(pid),
    queryFn: source(
      () => api.post<Simulation>(`/projects/${pid}/simulations`, { persist: true }),
      () => mock.mockSimulation,
    ),
    enabled: Boolean(pid),
    staleTime: 60_000,
  });
}

export function useReachability(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Reachability>({
    queryKey: queryKeys.reachability(pid),
    queryFn: source(
      () => api.get<Reachability>(`/projects/${pid}/simulations/reachability`),
      () => mock.mockReachability,
    ),
    enabled: Boolean(pid),
    staleTime: 60_000,
  });
}

/**
 * 模拟历史（F-5）：`GET /projects/{id}/simulations`。
 *
 * 列表只给摘要（**不含 `paths`**）：它回答"跑过哪几次、各自什么结论"，
 * 点开某一次才用 `useSimulationReport` 拉那份快照。
 */
export function useSimulations(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<SimulationSummary[]>({
    queryKey: queryKeys.simulationHistory(pid),
    queryFn: source(
      () => api.get<SimulationSummary[]>(`/projects/${pid}/simulations`),
      () => mock.mockSimulations,
    ),
    enabled: Boolean(pid),
    staleTime: 30_000,
  });
}

/**
 * 历史报告回看：按 **模拟 ID** 取当时持久化的快照。
 *
 * 报告是那次模拟的**快照**，产物后续变更不会改变它。
 * 入口 ID 现在由 `useSimulations` 提供（F-5 已补列表路由），不必再靠"本次模拟恰好有 ID"。
 */
export function useSimulationReport(simulationId: string | null) {
  return useQuery<Simulation>({
    queryKey: queryKeys.simulationReport(simulationId ?? ""),
    queryFn: source(
      () => api.get<Simulation>(`/simulations/${simulationId}/report`),
      () => mock.mockSimulation,
    ),
    enabled: Boolean(simulationId),
    staleTime: 5 * 60_000,
  });
}

/* --------------------------------------------------------- 生成 / 成本 / 运行 */

export function useRuns(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Run[]>({
    queryKey: queryKeys.runs(pid),
    queryFn: source(
      () => api.get<Run[]>(`/projects/${pid}/runs`),
      () => mock.mockRuns,
    ),
    enabled: Boolean(pid),
    staleTime: 10_000,
  });
}

/**
 * 单次运行详情。
 *
 * `GET /projects/{id}/runs` 与 `GET /runs/{id}` 返回**同构**的 `RunOut`；
 * 单条查询的价值在于「只轮询这一条」——进行中的运行不必为刷新状态重取整张列表。
 * `pollMs` 由调用方按是否终态决定（终态传 `undefined` 即停）。
 */
export function useRun(runId: string | null, pollMs?: number) {
  return useQuery<Run>({
    queryKey: queryKeys.run(runId ?? ""),
    queryFn: source(
      () => api.get<Run>(`/runs/${runId}`),
      () => {
        const found = mock.mockRuns.find((item) => item.id === runId);
        if (!found) throw new Error("离线数据源中没有这次运行");
        return found;
      },
    ),
    enabled: Boolean(runId),
    staleTime: 5_000,
    refetchInterval: pollMs,
  });
}

export function useCost(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<CostSummary>({
    queryKey: queryKeys.cost(pid),
    queryFn: source(
      () => api.get<CostSummary>(`/projects/${pid}/cost`),
      () => mock.mockCost,
    ),
    enabled: Boolean(pid),
    staleTime: 15_000,
  });
}

/**
 * 审计日志（`GET /projects/{id}/audit`）：运行 / 规则 / 人工决定合并成一条时间线。
 *
 * 只读聚合，后端不落新表——所以这里**没有**对应的 mutation。
 */
export function useProjectAudit(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<ProjectAudit>({
    queryKey: queryKeys.audit(pid),
    queryFn: source(
      () => api.get<ProjectAudit>(`/projects/${pid}/audit`),
      () => mock.mockProjectAudit,
    ),
    enabled: Boolean(pid),
    staleTime: 15_000,
  });
}

export function useCreateRun(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    // 失败已就位呈现：`GenerateView` 有 onError toast、`ScriptView` 渲染 `createRun.isError`
    meta: { errorHandled: true },
    mutationFn: (payload: {
      capability: string;
      task: string;
      inputs?: Record<string, Json>;
      writeTarget?: string;
      title?: string;
      idempotencyKey: string;
    }) =>
      api.post<Run>(
        `/projects/${pid}/runs`,
        {
          capability: payload.capability,
          task: payload.task,
          inputs: payload.inputs ?? {},
          write_target: payload.writeTarget,
          title: payload.title ?? "",
        },
        { idempotencyKey: payload.idempotencyKey },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.runs(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.cost(pid) });
      void client.invalidateQueries({ queryKey: ["projects", pid, "artifacts"] });
    },
  });
}

export function useCancelRun(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api.post<Run>(`/runs/${runId}/cancel`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.runs(pid) });
    },
  });
}

/** 重试只修复结构，不重生成全部内容（WORKFLOW §4）。仅 RETRYABLE_FAILED 可用。 */
export function useRetryRun(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api.post<Run>(`/runs/${runId}/retry`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.runs(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.cost(pid) });
    },
  });
}

/** 能力模块目录：驱动生成中心的能力选择，禁止前端硬编码能力名（AC-1406）。 */
export function useCapabilities() {
  return useQuery<CapabilityCatalog>({
    queryKey: queryKeys.capabilities,
    queryFn: source(
      () => api.get<CapabilityCatalog>("/capabilities"),
      () => mock.mockCapabilities,
    ),
    staleTime: 10 * 60_000,
  });
}

/* ------------------------------------------------------------ 来源 / 方法论 */

export function useSources(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<Source[]>({
    queryKey: queryKeys.sources(pid),
    queryFn: source(
      () => api.get<Source[]>(`/projects/${pid}/sources`),
      () => mock.mockSources,
    ),
    enabled: Boolean(pid),
    staleTime: 5 * 60_000,
  });
}

export function useCreateSource(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    // 失败已就位呈现：`SourceCreateModal` 既有 onError toast 也有 `createSource.isError`
    meta: { errorHandled: true },
    mutationFn: (payload: {
      kind: string;
      title: string;
      uri?: string;
      rights_status?: string;
      meta?: Record<string, Json>;
    }) => api.post<Source>(`/projects/${pid}/sources`, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.sources(pid) });
    },
  });
}

export function useMethodology() {
  return useQuery<Methodology>({
    queryKey: queryKeys.methodology,
    queryFn: source(
      () => api.get<Methodology>("/methodologies/current"),
      () => mock.mockMethodology,
    ),
    staleTime: 10 * 60_000,
  });
}

/** `GET /methodologies`：当前版本 + 已注册版本包清单（升级目标来自这里）。 */
export function useMethodologyList() {
  return useQuery<MethodologyList>({
    queryKey: queryKeys.methodologyList,
    queryFn: source(
      () => api.get<MethodologyList>("/methodologies"),
      () => mock.mockMethodologyList,
    ),
    staleTime: 5 * 60_000,
  });
}

/**
 * `GET /methodologies/{name}/{version}`：单版本包详情（规则 / 枚举 / schema 清单）。
 *
 * 版本详情是 **GET**；`POST /methodologies/{name}/publish` 才是发布，属方法论维护者操作。
 */
export function useMethodologyVersion(name: string | null, version: string | null) {
  const enabled = Boolean(name && version);
  return useQuery<Methodology>({
    queryKey: queryKeys.methodologyVersion(name ?? "", version ?? ""),
    queryFn: source(
      () => api.get<Methodology>(`/methodologies/${name}/${version}`),
      () => mock.mockMethodology,
    ),
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function useMethodologyUpgradePreview(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useMutation({
    // 失败提示自带：`MethodologyPanel` 的 `runPreview` 有 onError toast
    meta: { errorHandled: true },
    mutationFn: (targetVersion: string) =>
      api.post<MethodologyUpgradePreview>(`/projects/${pid}/methodology/preview`, undefined, {
        query: { target_version: targetVersion },
      }),
  });
}

export function useMigrateMethodology(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    // 失败提示自带：`MethodologyPanel` 的 `runMigrate` 有 onError toast
    meta: { errorHandled: true },
    mutationFn: (targetVersion: string) =>
      api.post<MethodologyMigration>(`/projects/${pid}/methodology/migrate`, undefined, {
        query: { target_version: targetVersion },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.project(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.overview(pid) });
    },
  });
}

/* ------------------------------------------------------------------ 导出 */

export function useExports(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<ExportJob[]>({
    queryKey: queryKeys.exports(pid),
    queryFn: source(
      () =>
        api
          .get<{ items: ExportJob[] }>(`/projects/${pid}/exports`)
          .then((page) => page.items ?? []),
      () => mock.mockExports,
    ),
    enabled: Boolean(pid),
    staleTime: 30_000,
  });
}

/**
 * 单个导出任务：用于对**进行中**的任务轮询到终态。
 *
 * 列表接口已经带回同样的 `ExportOut`，但一个还在排队/生成的导出需要反复刷新；
 * 轮询单条比反复重取整张导出历史更省。`pollMs` 由调用方按是否终态决定。
 */
export function useExportJob(exportId: string | null, pollMs?: number) {
  return useQuery<ExportJob>({
    queryKey: queryKeys.exportJob(exportId ?? ""),
    queryFn: source(
      () => api.get<ExportJob>(`/exports/${exportId}`),
      () => {
        const found = mock.mockExports.find((item) => item.id === exportId);
        if (!found) throw new Error("离线数据源中没有这条导出任务");
        return found;
      },
    ),
    enabled: Boolean(exportId),
    staleTime: 10_000,
    refetchInterval: pollMs,
  });
}

export function useExportPreview(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<ExportPreview>({
    queryKey: queryKeys.exportPreview(pid),
    queryFn: source(
      () => api.get<ExportPreview>(`/projects/${pid}/exports/preview`),
      () => mock.mockExportPreview,
    ),
    enabled: Boolean(pid),
    staleTime: 30_000,
  });
}

export function useCreateExport(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  const client = useQueryClient();
  return useMutation({
    // 失败提示自带：`ExportView` 的 `createExport` 有 onError toast
    meta: { errorHandled: true },
    mutationFn: (payload: { format: string; artifactIds?: string[]; idempotencyKey: string }) =>
      api.post<ExportJob>(
        "/exports",
        {
          project_id: pid,
          format: payload.format,
          artifact_ids: payload.artifactIds ?? [],
        },
        { idempotencyKey: payload.idempotencyKey },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.exports(pid) });
      void client.invalidateQueries({ queryKey: queryKeys.exportPreview(pid) });
    },
  });
}

/* ---------------------------------------------------------------- 系统 */

export function useSystemMetrics() {
  return useQuery<SystemMetrics>({
    queryKey: queryKeys.systemMetrics,
    queryFn: source(
      () => api.get<SystemMetrics>("/system/metrics"),
      () => mock.mockSystemMetrics,
    ),
    refetchInterval: 10_000,
  });
}

export function useHealth() {
  return useQuery<HealthStatus>({
    queryKey: queryKeys.health,
    queryFn: source(
      () => api.get<HealthStatus>("/health"),
      () => mock.mockHealth,
    ),
    refetchInterval: 30_000,
    retry: false,
  });
}

/** 就绪探针：区分「进程活着」与「依赖可用」（数据库 / 方法论包）。 */
export function useReady() {
  return useQuery<ReadyStatus>({
    queryKey: queryKeys.ready,
    queryFn: source(
      () => api.get<ReadyStatus>("/health/ready"),
      () => mock.mockReady,
    ),
    refetchInterval: 30_000,
    retry: false,
  });
}

export function useModelSettings() {
  return useQuery<ModelSettings>({
    queryKey: queryKeys.modelSettings,
    queryFn: () =>
      api.get<ModelSettings>("/settings/model", { headers: { "X-Model-Settings": "1" } }),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** Deliberately not a mutation: do not retain secrets in the mutation cache. */
export function saveModelSettings(payload: {
  provider: "onelinkai";
  protocol: "responses" | "chat_completions";
  base_url: string;
  model: string;
  enabled: boolean;
  send_temperature: boolean;
  revision: number;
  api_key?: string;
  clear_api_key?: boolean;
}) {
  return api.put<ModelSettings>("/settings/model", payload, {
    headers: { "X-Model-Settings": "1" },
  });
}

/**
 * 幂等键生成（宪法 B5 / V106）。
 *
 * **必须稳定**：后端按 `{project_id}:{run_key}` 查 `idempotency_keys` 表，
 * 命中即返回既有运行。若键里掺入时间戳，每次提交都会生成新键，
 * 幂等表形同虚设、重复扣费（V106 的设立目的即为此）。
 *
 * 因此键只由 `capability + task + 输入摘要` 决定：同一意图重复提交返回同一次运行；
 * 要真正发起新一次运行，请改变 task 或 inputs。
 */
export function makeRunKey(
  capability: string,
  task: string,
  inputs?: Record<string, unknown>,
): string {
  const seed = `${capability}::${task}::${digest(inputs ?? {})}`;
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) | 0;
  }
  return `${capability}-${Math.abs(hash).toString(36)}`;
}

/** 稳定摘要：键序归一后序列化，避免对象字面量顺序影响哈希。 */
function digest(value: unknown): string {
  return JSON.stringify(value, (_key, item) => {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      return Object.keys(item as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (item as Record<string, unknown>)[key];
          return acc;
        }, {});
    }
    return item;
  });
}

/* -------------------------------------------------------- 模拟历史比较 F-7 */

/**
 * 两次模拟结果的差异对比。
 *
 * `GET /simulations/{baseId}/compare?against={targetId}`
 * 需要同时提供 baseId 和 targetId 才会发起请求。
 */
export function useSimulationCompare(baseId: string | null, targetId: string | null) {
  return useQuery<SimulationCompare>({
    queryKey: queryKeys.simulationCompare(baseId ?? "", targetId ?? ""),
    queryFn: source(
      () =>
        api.get<SimulationCompare>(`/simulations/${baseId}/compare`, {
          query: { against: targetId! },
        }),
      () => mock.mockSimulationCompare(baseId ?? "", targetId ?? ""),
    ),
    enabled: Boolean(baseId && targetId) && baseId !== targetId,
    staleTime: 60_000,
  });
}

/* ------------------------------------------------------ 观众侧（specs/15 §3–§4） */

/**
 * 编辑端"观众洞察"的聚合读数（`GET /projects/{id}/audience/analytics`）。
 *
 * 口径只有一处（`specs/15` §4），前端只呈现、不重算——尤其**不自己算兴趣分**
 * （权重在 `packages/methodology` 的 HEU-012，后端算好回传）。
 */
export function useAudienceAnalytics(
  projectId?: string,
  groupBy: "storylet" | "story" | "project" = "storylet",
) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<AudienceAnalytics>({
    queryKey: queryKeys.audienceAnalytics(pid, groupBy),
    queryFn: source(
      () =>
        api.get<AudienceAnalytics>(`/projects/${pid}/audience/analytics`, {
          query: { group_by: groupBy },
        }),
      () => mock.mockAudienceAnalytics(groupBy),
    ),
    enabled: Boolean(pid),
    staleTime: 15_000,
  });
}

/** 操作记录（`GET /projects/{id}/audience/playthroughs`）：最近的游玩 + 逐步选择。 */
export function useAudiencePlaythroughs(projectId?: string) {
  const current = useCurrentProjectId();
  const pid = projectId ?? current;
  return useQuery<AudiencePlaythroughs>({
    queryKey: queryKeys.audiencePlaythroughs(pid),
    queryFn: source(
      () => api.get<AudiencePlaythroughs>(`/projects/${pid}/audience/playthroughs`),
      () => mock.mockAudiencePlaythroughs,
    ),
    enabled: Boolean(pid),
    staleTime: 15_000,
  });
}

/**
 * 离线演示模式下的**进程内**游玩状态（`VITE_USE_MOCK=1`）。
 *
 * 每个测试文件是独立模块实例，故它不会跨文件串味（RULES §6 的隔离跑法）。
 * 生产路径**不读它**——真后端的状态在库里。
 */
let mockPlay: mock.MockPlayState | null = null;

/**
 * 开始一次游玩（`POST /audience/playthroughs`）。
 *
 * 写操作**不回落**（RULES §11.2）：离线演示模式由 `VITE_USE_MOCK` 显式开关决定，
 * 不是失败后的兜底；调用点就地呈现失败（`meta.errorHandled`）。
 */
export function useStartPlaythrough() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (projectId: string): Promise<Playthrough> => {
      if (USE_MOCK) {
        const { state, view } = mock.mockPlaythroughStart(projectId);
        mockPlay = state;
        return view;
      }
      return api.post<Playthrough>("/audience/playthroughs", { project_id: projectId });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["projects"] });
      // 续玩块（`GET /audience/playthroughs`）也要刷新——否则新开的这次游玩
      // 不会出现在「继续上次旅程」里（前缀匹配即可覆盖所有 `status`）。
      void client.invalidateQueries({ queryKey: ["audience", "playthroughs"] });
    },
  });
}

/** 提交一步（`POST /audience/playthroughs/{id}/steps`）；`chosenKey` 省略＝叙事幕继续。 */
export function useSubmitPlayStep() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (args: {
      playthroughId: string;
      chosenKey?: string | null;
    }): Promise<Playthrough> => {
      if (USE_MOCK) {
        if (!mockPlay) throw new Error("离线演示模式没有进行中的游玩");
        const { state, view } = mock.mockPlaythroughStep(mockPlay, args.chosenKey ?? null);
        mockPlay = state;
        return view;
      }
      return api.post<Playthrough>(`/audience/playthroughs/${args.playthroughId}/steps`, {
        chosen_key: args.chosenKey ?? null,
      });
    },
    onSuccess: (view) => {
      void client.invalidateQueries({ queryKey: ["projects", view.project_id, "audience"] });
      // 步数 / 结束态变化 → 续玩块（`GET /audience/playthroughs`）必须重取，否则显示旧步数。
      void client.invalidateQueries({ queryKey: ["audience", "playthroughs"] });
    },
  });
}

/**
 * **主动放弃**一次游玩（`specs/15` §3 · §10 第 10 条）。
 *
 * 落点是播放器的「放弃这段旅程」——`ABANDONED` 此前**没有任何写入者**，
 * 于是它出现在 `status` 过滤可选值里却永远不会被产生。
 * ⚠️ 放弃**不丢已走的步数**（服务端保留 `play_steps` 与埋点），因此这里
 * 也**不重置** `steps_taken`。
 */
export function useAbandonPlaythrough() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (playthroughId: string): Promise<Playthrough> => {
      if (USE_MOCK) {
        if (!mockPlay) throw new Error("离线演示模式没有进行中的游玩");
        const { state, view } = mock.mockPlaythroughAbandon(mockPlay);
        mockPlay = state;
        return view;
      }
      return api.post<Playthrough>(`/audience/playthroughs/${playthroughId}/abandon`);
    },
    onSuccess: (view) => {
      // 结束态变化 → 续玩块必须重取：否则「继续上次旅程」仍指向这条已放弃的旅程。
      void client.invalidateQueries({ queryKey: ["audience", "playthroughs"] });
      void client.invalidateQueries({ queryKey: ["projects", view.project_id, "audience"] });
    },
  });
}

/* ---------------------------------------- 续玩（specs/15 §3 · PR-1518） */

/**
 * **本人**的游玩列表（`GET /audience/playthroughs`）。
 *
 * ⚠️ `enabled` 由调用方按登录态决定：该端点是**需登录**的（`ActorDep`），
 * 给匿名观众发请求只会得到 401。广场对未登录者不显示「继续上次旅程」。
 */
export function useMyPlaythroughs(enabled: boolean, status = "IN_PROGRESS") {
  return useQuery<MyPlaythroughs>({
    queryKey: queryKeys.myPlaythroughs(status),
    queryFn: source(
      () => api.get<MyPlaythroughs>("/audience/playthroughs", { query: { status } }),
      () => mock.mockMyPlaythroughs,
    ),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * **本人**的追更订阅（`GET /audience/follows`，`ADR-024`）。
 *
 * `enabled` 由调用方按登录态给（未登录不发请求，否则只会拿到 401）；
 * 订阅是**服务端实体**，不进 `store/ui.ts`（RULES §11.1）。
 */
export function useFollows(enabled: boolean) {
  return useQuery<Follows>({
    queryKey: queryKeys.follows,
    queryFn: source(
      () => api.get<Follows>("/audience/follows"),
      () => mock.mockFollows,
    ),
    enabled,
    staleTime: 30_000,
  });
}

/** 订阅一部作品（幂等）：`POST /audience/follows/{project_id}`。 */
export function useFollowProject() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (projectId: string): Promise<FollowState> => {
      if (USE_MOCK) return mock.mockFollowProject(projectId);
      return api.post<FollowState>(`/audience/follows/${projectId}`);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.follows });
    },
  });
}

/** 取消订阅（幂等）：`DELETE /audience/follows/{project_id}`。 */
export function useUnfollowProject() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (projectId: string): Promise<FollowState> => {
      if (USE_MOCK) return mock.mockUnfollowProject(projectId);
      return api.delete<FollowState>(`/audience/follows/${projectId}`);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.follows });
    },
  });
}

/** 恢复一次游玩：`GET /audience/playthroughs/{id}` → 当前幕 + 本幕可选。 */
export function useResumePlaythrough() {
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (playthroughId: string): Promise<Playthrough> => {
      if (USE_MOCK) return mock.mockResumePlaythrough(playthroughId);
      return api.get<Playthrough>(`/audience/playthroughs/${playthroughId}`);
    },
  });
}

/* ------------------------------------------------------ 共创社区（specs/15 §11） */

/**
 * 离线演示模式下的**进程内**脑洞状态（`VITE_USE_MOCK=1`）。
 *
 * 与 `mockPlay` 同源：每个测试文件是独立模块实例，故不跨文件串味；生产路径**不读它**。
 */
let mockIdeas: CommunityIdea[] | null = null;

function offlineIdeas(sort: "recommend" | "latest"): CommunityIdeas {
  if (!mockIdeas) mockIdeas = mock.mockCommunityIdeas.map((idea) => ({ ...idea }));
  const items = [...mockIdeas];
  if (sort === "recommend") {
    items.sort((a, b) => b.heat - a.heat || a.id.localeCompare(b.id));
  }
  return { items, total: items.length };
}

/**
 * 脑洞列表（`GET /community/ideas`）。**读公开**——未登录也能拿到；
 * 已登录时后端回传 `liked_by_me`。赞同数不在此重算（RULES §11：不缓存服务端实体）。
 */
export function useCommunityIdeas(sort: "recommend" | "latest" = "recommend") {
  return useQuery<CommunityIdeas>({
    queryKey: queryKeys.communityIdeas(sort),
    queryFn: source(
      () => api.get<CommunityIdeas>("/community/ideas", { query: { sort } }),
      () => offlineIdeas(sort),
    ),
    staleTime: 15_000,
  });
}

/** 「本周共创数据」脉冲（`GET /community/pulse`）：**只有两个有来源的数字**。 */
export function useCommunityPulse() {
  return useQuery<CommunityPulse>({
    queryKey: queryKeys.communityPulse,
    queryFn: source(
      () => api.get<CommunityPulse>("/community/pulse"),
      () => mock.mockCommunityPulse,
    ),
    staleTime: 30_000,
  });
}

/** 发布脑洞（`POST /community/ideas`，需登录）。写操作不回落（RULES §11.2）。 */
export function useCreateIdea() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (args: { text: string; ip?: string | null }): Promise<CommunityIdea> => {
      if (USE_MOCK) {
        if (!mockIdeas) mockIdeas = mock.mockCommunityIdeas.map((idea) => ({ ...idea }));
        const idea: CommunityIdea = {
          id: `IDEA_offline_${mockIdeas.length + 1}`,
          source: "普通观众 · 你",
          ip: args.ip ?? null,
          title: args.text.slice(0, 24),
          text: args.text,
          heat: 0,
          adopted: false,
          liked_by_me: false,
          created_at: null,
        };
        mockIdeas.unshift(idea);
        return idea;
      }
      return api.post<CommunityIdea>("/community/ideas", { text: args.text, ip: args.ip ?? null });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["community", "ideas"] });
    },
  });
}

/** 点赞 / 取消（`POST /community/ideas/{id}/like`，toggle，需登录）。 */
export function useToggleIdeaLike() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (ideaId: string): Promise<IdeaLikeResult> => {
      if (USE_MOCK) {
        if (!mockIdeas) mockIdeas = mock.mockCommunityIdeas.map((idea) => ({ ...idea }));
        const idea = mockIdeas.find((item) => item.id === ideaId);
        if (!idea) throw new Error("离线演示模式找不到该脑洞");
        idea.liked_by_me = !idea.liked_by_me;
        idea.heat += idea.liked_by_me ? 1 : -1;
        return { idea_id: ideaId, likes: idea.heat, liked: idea.liked_by_me };
      }
      return api.post<IdeaLikeResult>(`/community/ideas/${ideaId}/like`);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["community", "ideas"] });
    },
  });
}

/** 采纳为候选（`POST /community/ideas/{id}/adopt`，仅 editor；player 由后端 403）。 */
export function useAdoptIdea() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: async (ideaId: string): Promise<CommunityIdea> => {
      if (USE_MOCK) {
        if (!mockIdeas) mockIdeas = mock.mockCommunityIdeas.map((idea) => ({ ...idea }));
        const idea = mockIdeas.find((item) => item.id === ideaId);
        if (!idea) throw new Error("离线演示模式找不到该脑洞");
        idea.adopted = true;
        return idea;
      }
      return api.post<CommunityIdea>(`/community/ideas/${ideaId}/adopt`);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["community", "ideas"] });
    },
  });
}

/* ------------------------------------------------------ 账号与登录（specs/15 §2） */

/**
 * 当前登录用户（`GET /auth/me`）。
 *
 * - 无 token：`me()` 直接返回 `null`（未登录，**不是错误**）——访问控制据此重定向 `/login`；
 * - 有 token：读后端；401 由 `me()` 清 token 并返回 `null`（不静默降级成假身份）；
 * - 离线演示态（`VITE_USE_MOCK=1`）：返回编辑账号（显式开关，不是失败后的回落）。
 */
export function useSession() {
  return useQuery<SessionUser | null>({
    queryKey: queryKeys.session,
    queryFn: source(fetchMe, () => mock.mockSessionUser),
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * 退出登录：调 `POST /auth/logout` 撤销服务端会话并清本地 token。
 *
 * 成功后 `client.clear()` —— 换身份后**不得复用**上一个账号的服务端实体缓存。
 */
export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: () => apiLogout(),
    onSuccess: () => {
      client.clear();
    },
  });
}

/** 账号列表（`GET /auth/users`，仅 admin）：「开通编辑账号」的选择来源。 */
export function useUsers(enabled: boolean) {
  return useQuery<SessionUser[]>({
    queryKey: queryKeys.users,
    queryFn: source(
      () => api.get<SessionUser[]>("/auth/users"),
      () => mock.mockUsers,
    ),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

/** 开通编辑账号（`POST /auth/users/{id}/grant-editor`，仅 admin）。 */
export function useGrantEditor() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (args: { userId: string; creationRole: string }) =>
      api.post<SessionUser>(`/auth/users/${args.userId}/grant-editor`, {
        creation_role: args.creationRole,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.users });
    },
  });
}

/* --------------------------------- 角色设置 / 切换 / 留痕（specs/15 §2 · ADR-025） */

/**
 * 切换**当前身份**（`POST /auth/me/switch-view`）。
 *
 * 成功后**必须失效 `session`**：头部、`RequireAuth`、侧栏都读它，
 * 不失效就会出现"界面说已切换、访问控制仍按旧身份判"的错位（症状是切了却进不去创作台）。
 */
export function useSwitchView() {
  const client = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (viewMode: "player" | "editor") => apiSwitchView(viewMode),
    onSuccess: (user) => {
      client.setQueryData(queryKeys.session, user);
      void client.invalidateQueries({ queryKey: queryKeys.roleChanges() });
    },
  });
}

/**
 * admin 设置任意账号的授权（`POST /auth/users/{id}/set-role`，可升可降）。
 *
 * 成功后会改的是**别人**的身份，故失效 `users` 与角色变更记录；
 * 若改的是自己，后端返回的就是新身份 ⇒ 一并同步 `session`
 *（否则本人下一次请求会带着旧身份，界面与后端不一致）。
 */
export function useSetUserRole() {
  const client = useQueryClient();
  const session = useSession();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (args: { userId: string; input: SetRoleInput }) =>
      apiSetUserRole(args.userId, args.input),
    onSuccess: (updated) => {
      void client.invalidateQueries({ queryKey: queryKeys.users });
      void client.invalidateQueries({ queryKey: ["auth", "role-changes"] });
      if (updated.id === session.data?.id) {
        client.setQueryData(queryKeys.session, updated);
      }
    },
  });
}

/**
 * 角色变更记录（`GET /auth/role-changes`）。
 *
 * `userId` 缺省时**不在前端补**"看自己"的条件——归属由服务端按身份判
 * （`specs/15` §3「我的游玩」同一条纪律：前端过滤等于没有过滤）。
 */
export function useRoleChangeLogs(userId?: string, enabled = true) {
  return useQuery<RoleChangeLog[]>({
    queryKey: queryKeys.roleChanges(userId),
    queryFn: source(
      () => fetchRoleChangeLogs(userId),
      () =>
        userId
          ? mock.mockRoleChangeLogs.filter((row) => row.user_id === userId)
          : mock.mockRoleChangeLogs,
    ),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}
